"use client";

import Link from "next/link";
import { ArrowRight, Cpu, Brain, Users, Globe, Briefcase, Compass, Lightbulb, GraduationCap } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { careers as data } from "@/lib/data/about";

const iconMap = { Cpu, Brain, Users, Globe } as const;
const cultureIcons = [Briefcase, Compass, GraduationCap] as const;
const areaAccents = [
  "var(--color-crimson)",
  "var(--color-purple)",
  "var(--color-cyan)",
  "var(--color-amber)",
] as const;

export function Careers() {
  return (
    <Section id="careers" className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-96 w-96 rounded-full bg-[var(--color-purple)] opacity-20 blur-[140px]" />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-purple)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>

        <div className="mt-12 grid sm:grid-cols-2 gap-5">
          {data.whyJoin.map((w, i) => {
            const Icon = iconMap[w.icon as keyof typeof iconMap] ?? Cpu;
            const accent = areaAccents[i % areaAccents.length]!;
            return (
              <Reveal key={w.title} delay={i * 80}>
                <div
                  className="glass rounded-2xl p-6 h-full"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 12%, transparent)`,
                  }}
                >
                  <span
                    className="grid place-items-center h-11 w-11 rounded-xl"
                    style={{
                      background: `color-mix(in oklch, ${accent} 18%, transparent)`,
                      boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)`,
                    }}
                  >
                    <Icon className="h-5 w-5" style={{ color: accent }} />
                  </span>
                  <h3 className="mt-4 text-lg">{w.title}</h3>
                  <p className="mt-3 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{w.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={400}>
          <div className="mt-10 glass rounded-2xl p-6">
            <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))]">Open Roles</div>
            <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {data.openRoles.map((role, i) => {
                const accent = areaAccents[i % areaAccents.length]!;
                return (
                  <div
                    key={role.area}
                    className="p-4 rounded-xl glass"
                    style={{
                      boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 12%, transparent)`,
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-sm">{role.area}</h3>
                      <span
                        className="eyebrow-tiny font-bold"
                        style={{ color: accent }}
                      >
                        {role.count}
                      </span>
                    </div>
                    <div className="text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{role.focus}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>

        <Reveal delay={500}>
          <div className="mt-10 glass rounded-2xl p-6">
            <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))]">Culture</div>
            <div className="mt-5 grid sm:grid-cols-3 gap-5">
              {data.culture.map((c, i) => {
                const Icon = cultureIcons[i % cultureIcons.length]!;
                return (
                  <div key={c.heading} className="flex items-start gap-3">
                    <span
                      className="grid place-items-center h-9 w-9 rounded-lg shrink-0"
                      style={{
                        background: "color-mix(in oklch, var(--color-purple) 18%, transparent)",
                        boxShadow: "inset 0 0 0 1px color-mix(in oklch, var(--color-purple) 35%, transparent)",
                      }}
                    >
                      <Icon className="h-4 w-4 text-[var(--color-purple)]" />
                    </span>
                    <div>
                      <h3 className="text-sm font-bold">{c.heading}</h3>
                      <p className="text-xs text-[rgb(var(--fg-muted))] mt-1 leading-relaxed">{c.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>

        <Reveal delay={600}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link
                key={c.label}
                href={c.href}
                className={
                  i === 0
                    ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                    : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                }
              >
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
